import { useState, useEffect } from 'react';
import type { SyntheticEvent } from 'react';
import { bisector } from 'd3-array';
import { localPoint } from '@visx/event';
import { getTimeSeriesPeriodMap } from '@/utilities/timeSeriesPeriodMap';

import type { ScaleTime, ScaleLinear } from 'd3-scale';
import { useTheme } from '@simplywallst/ui-core';
import { format } from 'date-fns';
import { toCurrencyTruncated } from '@/utilities/formatting';

import { Wrapper, Now, Label } from './styled';

interface Props {
  height: number;
  keys: number[];
  entities: Record<number, number | null>;
  periods: ReturnType<typeof getTimeSeriesPeriodMap>;
  positionX: number;
  currencyISO: string;
  xScale: ScaleTime<number, number>;
  yScale: ScaleLinear<number, number>;
  onClick?: () => void;
}

const bisectDate = bisector((d) => d).left;

export const Marker = ({
  height,
  positionX,
  keys,
  currencyISO,
  entities,
  periods,
  xScale,
  yScale,
  onClick,
}: Props) => {
  const theme = useTheme();
  const [index, setIndex] = useState<number>(bisectDate(keys, periods.NOW));
  const [hover, setHover] = useState<boolean>(false);
  const nextPeriods = getTimeSeriesPeriodMap(
    new Date(keys[index] ? keys[index] : periods.NOW)
  );
  const x = xScale(nextPeriods.NOW);

  useEffect(() => {
    if (keys.length > 0 && index > keys.length - 1) {
      setIndex(keys.length - 1);
    }
  }, [keys, index]);

  const handleMove = (e) => {
    const point = localPoint(e);

    let x = 0;
    if (point !== null && point.x >= 0) {
      x = point.x;
    } else if (point !== null && point.x < 0) {
      // Fallback if Firefox has a negative point.x
      const rect = e.target.getBoundingClientRect();
      x = e.clientX - rect.x;
    }

    const date = xScale.invert(x);
    const nextIndex = bisectDate(keys, date);
    if (nextIndex !== index && nextIndex <= keys.length - 1) {
      setIndex(nextIndex);
    }
  };

  const handleHover = (e: SyntheticEvent) => {
    if (e.type === 'mouseenter' || e.type === 'touchstart') {
      setHover(true);
    }
    if (e.type === 'mouseleave' || e.type === 'touchend') {
      setHover(false);
    }
  };

  return (
    <Wrapper>
      <Label hover={hover}>
        <div>
          <span>
            {keys[index] ? format(keys[index], 'MMM dd yyyy') : `n/a`}
          </span>
          <b>
            {toCurrencyTruncated(entities[keys[index]] || 0, 2, currencyISO)}
          </b>
        </div>
      </Label>
      <svg
        width="100%"
        height={height}
        transform={`translate(${positionX}, 0)`}
      >
        {keys.length > 0 && (
          <>
            <Now
              y="0"
              x={x}
              width="1"
              height={height}
              fill={theme.color.brand01}
              hover={hover}
            />
            {hover && (
              <>
                <g transform={`translate(${x}, 0)`}>
                  <rect
                    x="0"
                    y="0"
                    width="12"
                    height="12"
                    fill="white"
                    clipPath="url(#tooltip-arrow-clipping)"
                    transform="rotate(180, 6, 6) translate(5.5, 0)"
                  />
                </g>
                <circle
                  transform={`translate(${x} ${yScale(
                    entities[keys[index]] || 0
                  )})`}
                  r="2"
                  fill={theme.color.brand01}
                  stroke="white"
                  strokeWidth="1"
                />
              </>
            )}
            <rect
              width="100%"
              height="100%"
              fill="transparent"
              onMouseMove={handleMove}
              onMouseEnter={handleHover}
              onMouseLeave={handleHover}
              onTouchMove={handleMove}
              onTouchStart={handleHover}
              onTouchEnd={handleHover}
              onClick={onClick}
            />
          </>
        )}
      </svg>
    </Wrapper>
  );
};
